import React from 'react';
import { SearchFilters } from '../types';
import { stateAbbreviations } from '../services/listingHelpers';

interface Props {
  filters: SearchFilters;
  onChange: (filters: SearchFilters) => void;
  isMobile?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

const treatmentTypes = [
  'Detox', 'Inpatient', 'Outpatient', 'Residential',
  'Partial Hospitalization', 'Intensive Outpatient', 'Sober Living',
];

const insuranceOptions = [
  'Private Insurance', 'Medicare', 'Medicaid',
  'Aetna', 'Blue Cross Blue Shield', 'Cigna', 'United Healthcare',
];

const FilterSidebar: React.FC<Props> = ({ filters, onChange, isMobile, isOpen, onClose }) => {
  const update = (key: keyof SearchFilters, value: any) =>
    onChange({ ...filters, [key]: value || undefined });

  const sectionStyle: React.CSSProperties = {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid #eee',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '11px', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.05em', color: '#555',
    marginBottom: '8px',
  };

  const selectStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px', fontSize: '14px',
    border: '1px solid #ddd', borderRadius: '6px', background: '#fff', outline: 'none',
  };

  const content = (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Filters</h3>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button onClick={() => onChange({})} style={{ background: 'none', border: 'none', color: '#1a237e', cursor: 'pointer', fontSize: '13px' }}>
            Clear all
          </button>
          {isMobile && onClose && (
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1, color: '#333' }}>×</button>
          )}
        </div>
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>State</label>
        <select style={selectStyle} value={filters.state || ''} onChange={e => update('state', e.target.value)}>
          <option value="">All States</option>
          {Object.entries(stateAbbreviations).sort((a, b) => a[1].localeCompare(b[1])).map(([abbr, name]) => (
            <option key={abbr} value={abbr}>{name}</option>
          ))}
        </select>
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>Treatment Type</label>
        <select style={selectStyle} value={filters.treatmentType || ''} onChange={e => update('treatmentType', e.target.value)}>
          <option value="">All Types</option>
          {treatmentTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>Insurance Accepted</label>
        <select style={selectStyle} value={filters.insurance || ''} onChange={e => update('insurance', e.target.value)}>
          <option value="">Any Insurance</option>
          {insuranceOptions.map(i => <option key={i} value={i}>{i}</option>)}
        </select>
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>Setting</label>
        <select style={selectStyle} value={filters.environmentalSetting || ''} onChange={e => update('environmentalSetting', e.target.value)}>
          <option value="">Any Setting</option>
          <option value="Residential">Residential</option>
          <option value="Luxury">Luxury</option>
          <option value="Urban">Urban</option>
          <option value="Rural">Rural</option>
          <option value="Suburban">Suburban</option>
        </select>
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>Gender</label>
        <select style={selectStyle} value={filters.gender || ''} onChange={e => update('gender', e.target.value)}>
          <option value="">All Genders</option>
          <option value="Men Only">Men Only</option>
          <option value="Women Only">Women Only</option>
          <option value="Co-ed">Co-ed</option>
        </select>
      </div>

      <div>
        <label style={labelStyle}>Minimum Rating</label>
        <select style={selectStyle} value={filters.minRating || ''} onChange={e => update('minRating', e.target.value ? Number(e.target.value) : undefined)}>
          <option value="">Any Rating</option>
          <option value="4.5">4.5+ (Excellent)</option>
          <option value="4.0">4.0+ (Great)</option>
          <option value="3.5">3.5+ (Very Good)</option>
          <option value="3.0">3.0+ (Good)</option>
        </select>
      </div>
    </>
  );

  if (isMobile) {
    if (!isOpen) return null;
    return (
      <>
        <div className="filter-overlay" onClick={onClose} />
        <div className="filter-drawer">{content}</div>
      </>
    );
  }

  return (
    <aside style={{ width: '240px', flexShrink: 0, padding: '16px', background: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0', height: 'fit-content' }}>
      {content}
    </aside>
  );
};

export default FilterSidebar;
